/* src/components/admin/AdminLogin.js */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../../firebaseConfig'; // Assuming this exports your initialized auth
import { useLoading } from '../../contexts/LoadingContext';
import { useProfile } from '../../contexts/ProfileContext'; // To check if already logged in as admin

const AdminLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const { showLoading, hideLoading } = useLoading();
    const { authUser, isAuthLoading } = useProfile(); // Get current user state

    // Redirect if already logged in as admin
    useEffect(() => {
        if (!isAuthLoading && authUser) {
            authUser.getIdTokenResult().then(idTokenResult => {
                if (idTokenResult.claims.isAdmin === true) {
                    console.log('[AdminLogin] Already logged in as admin. Redirecting to dashboard.');
                    navigate('/admin/dashboard', { replace: true });
                }
                // If logged in but not admin, stay here (they might want to log out and log in as admin)
            }).catch(err => {
                 console.error("[AdminLogin] Error checking existing admin claims:", err);
                 // Stay on login page on error
            });
        }
    }, [authUser, isAuthLoading, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        if (!email || !password) {
            setError('Please enter both email and password.');
            return;
        }
        showLoading('Attempting admin login...');

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            console.log('[AdminLogin] Firebase login successful for:', user.email);

            // --- CRITICAL: Force refresh token to get latest claims ---
            console.log('[AdminLogin] Forcing token refresh to check admin claim...');
            const idTokenResult = await user.getIdTokenResult(true); // Force refresh!
            console.log('[AdminLogin] Token refreshed. Claims:', idTokenResult.claims);

            if (idTokenResult.claims.isAdmin === true) {
                console.log('[AdminLogin] Admin claim verified. Navigating to dashboard.');
                hideLoading();
                // Redirect to the intended destination or admin dashboard
                const from = location.state?.from?.pathname || '/admin/dashboard';
                navigate(from, { replace: true });
            } else {
                console.warn('[AdminLogin] Login successful, but user is NOT an admin. Signing out.');
                setError('Access Denied. You do not have admin privileges.');
                await signOut(auth); // Sign out the non-admin user
                hideLoading();
            }
        } catch (err) {
            console.error('[AdminLogin] Firebase Login Error:', err.code, err.message);
            let friendlyMessage = 'Login failed. Please check your credentials.';
            if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                friendlyMessage = 'Invalid email or password.';
            } else if (err.code === 'auth/invalid-email') {
                friendlyMessage = 'Please enter a valid email address.';
            } else if (err.code === 'auth/too-many-requests') {
                 friendlyMessage = 'Access temporarily disabled due to too many failed login attempts. Please reset your password or try again later.';
            }
            setError(friendlyMessage);
            hideLoading();
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-800 px-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-sm border border-indigo-200">
                <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
                    Admin Login
                </h1>

                <form onSubmit={handleLogin}>
                    <div className="mb-4">
                        <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address
                        </label>
                        <input
                            type="email"
                            id="admin-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="admin@example.com"
                            aria-label="Admin Email Address"
                        />
                    </div>

                    <div className="mb-6">
                        <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700 mb-1">
                            Password
                        </label>
                        <input
                            type="password"
                            id="admin-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="••••••••"
                            aria-label="Admin Password"
                        />
                    </div>

                    {error && (
                        <p className="mb-4 text-sm text-center text-red-600 bg-red-50 p-3 rounded border border-red-200" role="alert">
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        className="w-full mt-2 py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Sign In
                    </button>
                </form>

                 {/* Optional: Link back to the main app login if applicable */}
                 {/* <div className="mt-6 text-sm text-center">
                    <a href="/login" className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline">
                        Back to main site
                    </a>
                </div> */}
            </div>
        </div>
    );
};

export default AdminLogin;